import type { MouseEvent } from "react";
import type { Lang } from "../_home/dict";

/**
 * Anchor-first navigation helpers. Every page is prerendered at a real URL
 * (`/trip/<slug>/`, `/personas/<slug>/`, `/` and their `/es` variants), so
 * navigation is plain `<a href>` that works without JS and is crawlable for
 * SEO. The in-app pushState router is layered on top purely as a progressive
 * enhancement: an onClick intercepts a *plain* left-click for an instant
 * in-place transition, while the real href stays the fallback.
 */

const prefix = (lang: Lang): string => (lang === "es" ? "/es" : "");

/** Homepage href for the active locale (`/` or `/es/`). */
export const homeHref = (lang: Lang): string => {
  const p = prefix(lang);
  return p === "" ? "/" : `${p}/`;
};

/** Trip page href, matching the prerendered `/trip/<slug>/` route. */
export const tripHref = (lang: Lang, slug: string): string =>
  `${prefix(lang)}/trip/${encodeURIComponent(slug)}/`;

/** Persona landing href, matching the prerendered `/personas/<slug>/` route. */
export const personaHref = (lang: Lang, persona: string): string =>
  `${prefix(lang)}/personas/${encodeURIComponent(persona)}/`;

/**
 * True for a plain left-click with no modifier keys — the only case where we
 * intercept an anchor for in-app (pushState) navigation. Cmd/Ctrl/Shift/Alt
 * and middle-clicks fall through to the browser so "open in new tab",
 * "download", etc. keep working as on any normal link.
 */
export function isPlainClick(e: MouseEvent<HTMLAnchorElement>): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}
